/**
 * Verbatim prototype copy for the page view (spec B §1.3 / §1.4 / §1.7) and a
 * few pure page helpers shared with the category view's page rows.
 */

export const PAGE_COPY = Object.freeze({
    edit: 'Edit',
    continueEditing: 'Continue editing',
    requiredPill: 'Required reading',
    draftPill: 'Draft · only you and admins can see it',
    draftBanner: "This page is a draft. Only you and admins can see it, and the AI can't use it yet.",
    // EXTRA (production state the prototype has no copy for): a published page
    // with an unpublished draft (after Restore, or a suggestion accepted into
    // a draft). Writers only — the server sends `has_draft` to writers only.
    pendingDraftBanner: 'There are unpublished changes to this page. Publish them to make them current.',
    ackButton: 'I have read and understood',
    suggestTitle: 'The AI Brain suggests clearer wording',
    suggestBody: 'People never see this text as-is: the AI rewrites it in answers. Fixing the page helps everyone who reads it directly.',
    suggestNow: 'Now',
    suggestNew: 'Suggested',
    suggestDismiss: 'Dismiss',
    suggestAccept: 'Accept as new version',
    suggestAcceptToast: 'Saved as a new version. The AI will use the new wording in about a minute.',
    // EXTRA: the page already has unpublished edits, so the suggestion goes into that draft.
    suggestAcceptDraftToast: 'Added the suggested wording to your draft. Publish it to make it current.',
    suggestDismissToast: "Dismissed. The Brain won't suggest this edit again.",
    askTitle: 'Ask about this page',
    askBody: 'The answer cites the exact passage, and only uses pages you can read.',
    tocLabel: 'On this page',
    accessLabel: 'Access',
    accessBody: 'Choose who can view and edit this page. Without its own rules it uses its category\'s access.',
    accessButton: 'Manage access',
    tocEmpty: 'No headings on this page yet.',
    acksLabel: 'Acknowledgements',
    remind: 'Remind the rest',
    versionsLabel: 'Versions',
    versionsEmpty: 'No published versions yet.',
    current: 'Current',
    compare: 'Compare',
    restore: 'Restore',
    emptyBody: 'This page has no content yet.',
    notFoundTitle: "This page isn't available",
    notFoundBody: "It may have been archived or moved, or you don't have access to it.",
    restoreOverDraftConfirm: 'This replaces the unpublished changes in the current draft with the restored version. Continue?',
});

/** "This page is required reading. Confirm you've read v4." */
export const needAckNote = (v) => `This page is required reading. Confirm you've read v${v}.`;
/** "You acknowledged v4. If the page changes, you'll be asked again." */
export const ackedNote = (v) => `You acknowledged v${v}. If the page changes, you'll be asked again.`;
/** Toast after "I have read and understood". */
export const ackToast = (title, v) => `Acknowledged ${title} v${v}. If it changes, you'll be asked again.`;
/** "17 of 24 people have acknowledged". */
export const readLabel = (readBy, readOf) => `${readBy} of ${readOf} people have acknowledged`;
/** Toast after "Remind the rest" (singular fixed, like the foundation's "1 page"). */
export const remindToast = (count, v) => (count === 1
    ? `Reminder sent to 1 person who hasn't acknowledged v${v}.`
    : `Reminder sent to ${count} people who haven't acknowledged v${v}.`);
/** Toast after Restore: "Restored v2 as a new draft (v5). Publish it to make it current." */
export const restoreToast = (oldV, draftV) => `Restored v${oldV} as a new draft (v${draftV}). Publish it to make it current.`;
/** Toast after Compare: "Comparing v2 with v4." */
export const compareToast = (oldV, currentV) => `Comparing v${oldV} with v${currentV}.`;
/** `Only you and admins see this · section "Before you send a proposal"`. */
export const suggestMeta = (section) => (section
    ? `Only you and admins see this · section "${section}"`
    : 'Only you and admins see this');
/** Compare card heading. */
export const compareHeading = (oldV, currentV) => `Comparing v${oldV} with v${currentV}`;

/**
 * The version a reader sees for a page. A never-published draft is stored as
 * v0 by the API; the prototype shows it as v1 (it becomes v1 on publish).
 */
export function pageVersion(page) {
    const v = Number(page && page.version) || 0;
    if (v < 1 && page && page.status === 'draft') return 1;
    return v;
}

/** The version an acknowledgement confirms (`ack_version`, else the page version). */
export function ackVersion(page) {
    return Number(page && page.ack_version) || pageVersion(page);
}

/** Required reading that can be acknowledged right now (published + required). */
export function isAckable(page) {
    return Boolean(page && page.required_ack && page.status === 'published');
}

/**
 * A readable message for a Wiki API error: the structured `detail.message`,
 * a human sentence `detail`, else `fallback` (never a bare machine code).
 * Plan refusals (403 `wiki_feature_disabled` / `wiki_limit_reached`, PLAN-CP
 * §2) carry user-facing `detail.message` copy, so every write toasts it.
 */
export function wikiErrorMessage(error, fallback = 'Something went wrong. Please try again.') {
    const detail = error && error.response && error.response.data && error.response.data.detail;
    if (detail && typeof detail === 'object' && !Array.isArray(detail) && typeof detail.message === 'string' && detail.message.trim()) {
        return detail.message;
    }
    if (typeof detail === 'string' && detail.trim() && !/^[a-z0-9_.:-]+$/.test(detail.trim())) return detail;
    return fallback;
}
